// Synthetic grids for the test runner. The hosted app reads a real covariate
// stack. These functions exist so the map and fertility tests have cell geometry
// without a parquet blob.

export function mockStackCells(b: [number, number, number, number]): {
  geoms: { type: string; coordinates: unknown }[];
  centroids: { lng: number; lat: number }[];
  features: (number | null)[][];
} {
  const G = 24;
  const west = b[0] + (b[2] - b[0]) * 0.08;
  const south = b[1] + (b[3] - b[1]) * 0.08;
  const dLng = ((b[2] - b[0]) * 0.84) / G;
  const dLat = ((b[3] - b[1]) * 0.84) / G;
  const geoms: { type: string; coordinates: unknown }[] = [];
  const centroids: { lng: number; lat: number }[] = [];
  const band: number[] = [];
  const bowl: number[] = [];
  const grad: number[] = [];
  for (let r = 0; r < G; r++) {
    for (let c = 0; c < G; c++) {
      const w = west + c * dLng;
      const s = south + r * dLat;
      geoms.push({
        type: "Polygon",
        coordinates: [
          [
            [w, s],
            [w + dLng, s],
            [w + dLng, s + dLat],
            [w, s + dLat],
            [w, s],
          ],
        ],
      });
      centroids.push({ lng: w + dLng / 2, lat: s + dLat / 2 });
      const wiggle = Math.sin(r * 3.1 + c * 7.3) * 0.35; // deterministic texture
      band.push([2, 5, 9][Math.min(2, Math.floor((c / G) * 3))] + wiggle);
      bowl.push(Math.hypot(r - G / 2, c - G / 2) + wiggle);
      grad.push(r * 0.4 + wiggle);
    }
  }
  return { geoms, centroids, features: [band, bowl, grad] };
}

export function mockPropertyValues(
  centroids: { lng: number; lat: number }[],
  prop: string,
): (number | null)[] {
  const n = centroids.length;
  const g = Math.max(1, Math.round(Math.sqrt(n)));
  return centroids.map((_, i) => {
    const r = Math.floor(i / g) / g; // 0..1 south->north
    const c = (i % g) / g; // 0..1 west->east
    const wiggle = Math.sin(i * 2.7) * 0.05;
    switch (prop) {
      case "ph":
        return Math.round((5.2 + r * 1.6 + wiggle * 4) * 100) / 100; // acidic south band
      case "p":
        return Math.round(6 + c * 30 + wiggle * 60); // P-poor west edge
      case "k":
        return Math.round(
          70 + Math.hypot(r - 0.5, c - 0.5) * 160 + wiggle * 100,
        ); // low center bowl
      case "cec":
        return Math.round((10 + r * 8 + wiggle * 20) * 10) / 10;
      case "om":
        return Math.round((2 + r * 1.5 + wiggle * 4) * 10) / 10;
      case "ca":
        return Math.round(1400 + r * 1600 + wiggle * 1200); // ppm — rises N, drives Ca:Mg
      case "mg":
        return Math.round(120 + c * 140 + wiggle * 200); // ppm — low on the west side (Ca:Mg wide there)
      default:
        return null;
    }
  });
}

export function mockYieldValues(
  centroids: { lng: number; lat: number }[],
  crop: string,
  year: number,
): (number | null)[] {
  const base = crop === "soybean" ? 60 : crop === "wheat" ? 75 : 190;
  const spread = crop === "soybean" ? 25 : 70;
  const yearFactor = 0.85 + (year % 5) * 0.06;
  const n = centroids.length;
  const g = Math.max(1, Math.round(Math.sqrt(n)));
  return centroids.map((_, i) => {
    const r = Math.floor(i / g) / g;
    const c = (i % g) / g;
    const spatial =
      0.7 + 0.6 * (0.5 * r + 0.5 * (1 - Math.hypot(r - 0.5, c - 0.5)));
    return Math.round(
      base * yearFactor * spatial + Math.sin(i * 1.3) * spread * 0.1,
    );
  });
}

export function mockCovariateValues(
  centroids: { lng: number; lat: number }[],
  min: number,
  max: number,
): (number | null)[] {
  const n = centroids.length;
  const g = Math.max(1, Math.round(Math.sqrt(n)));
  const span = max - min || 1;
  return centroids.map((_, i) => {
    const r = Math.floor(i / g) / g;
    const c = (i % g) / g;
    const t = 0.5 + 0.5 * Math.sin(r * 3.1 + 0.7) * Math.cos(c * 2.6 + 0.3);
    return Math.round((min + t * span) * 100) / 100;
  });
}
